import { Component, Input, Output, EventEmitter, OnInit, forwardRef, SkipSelf, Host, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlContainer,
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors, ValidatorFn
} from '@angular/forms';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from "primeng/inputnumber";
import { CheckboxComponent } from "../checkbox/checkbox.component";
import { CheckboxChangeEvent } from "primeng/checkbox";

@Component({
  selector: 'app-input-plus-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputWrapperComponent,
    DropdownModule,
    InputTextModule,
    ButtonModule,
    InputNumberModule,
    CheckboxComponent,
    ReactiveFormsModule
  ],
  templateUrl: './input-plus-picker.component.html',
  styleUrls: ['./input-plus-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputPlusPickerComponent),
      multi: true
    }
  ]
})
export class InputPlusPickerComponent implements OnInit, ControlValueAccessor {
  @Input() formControlName: string
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() value: Date;
  @Input() defaultToUnknown: boolean = true;
  @Input() errorMessage?: ValidationErrors | null;
  @Input() touched?: boolean;
  @Input() disabled: boolean = false;

  protected numericValue: number;
  protected unitValue: string | null;
  protected unknownDate: boolean;


  dateOptions = [
    { label: $localize`:@@date.days:Days`, value: 'days' },
    { label: $localize`:@@date.weeks:Weeks`, value: 'weeks' },
    { label: $localize`:@@date.months:Months`, value: 'months' },
    { label: $localize`:@@date.years:Years`, value: 'years' },
  ];

  constructor(@Optional() @Host() @SkipSelf() protected controlContainer: ControlContainer) {
  }

  // We create the validator in this way so that it has this component as its context and can access unknownDate and value.
  public getFirstNoticedDateError(): ValidatorFn {
    return (): ValidationErrors | null => {
      if (!this) {
        // validation might trigger before initialization.
        return undefined;
      }

      if (!this.unknownDate && !this.value) {
        return {
          dateOrUnknownRequired: true
        } as ValidationErrors
      }
      // no error => undefined
      return undefined;
    }
  }

  ngOnInit() {
    this.controlContainer.control.get(this.formControlName).setValidators(this.getFirstNoticedDateError())
    this.initFields()
  }

  private initFields() {
    if (this.value) {
      const now = Date.now();
      const diff = now - new Date(this.value).getTime();
      if (diff > 0) {
        this.numericValue = Math.floor(diff / (24 * 60 * 60 * 1000));
        this.unitValue = 'days';
      }
    } else {
      this.unknownDate = this.defaultToUnknown;
    }
  }

  onCheckboxChange(event: CheckboxChangeEvent): void {
    if (event.checked) {
      this.numericValue = null;
      this.unitValue = null;
    }
    this.calculateValue()
  }

  calculateValue() {
    if (!!this.numericValue && this.unitValue) {
      this.value = subtractFromNow(this.numericValue, this.unitValue);
    } else {
      this.value = null;
    }

    this.onChange(this.value)
    this.onTouched()
  }

  onChange = (value: any) => {
  };
  onTouched = () => {
  };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: any): void {
    this.initFields()
  }
}

/** Helper to subtract a numeric offset from now in a given unit. */
function subtractFromNow(value: number, unit: string): Date {
  const now = Date.now();
  let millis = 0;

  switch (unit) {
    case 'days':
      millis = value * 24 * 60 * 60 * 1000; // # of days in ms
      break;
    case 'weeks':
      millis = value * 7 * 24 * 60 * 60 * 1000;
      break;
    case 'months':
      // approximate months as 30 days, or do a more advanced approach
      millis = value * 30 * 24 * 60 * 60 * 1000;
      break;
    case 'years':
      // approximate a year as 365 days
      millis = value * 365 * 24 * 60 * 60 * 1000;
      break;
    default:
      millis = 0;
      break;
  }

  return new Date(now - millis);
}
